import { createSelector } from 'reselect'
import { estimatedCommission } from 'manager/util/helper'
import { isBefore, parse, startOfToday } from 'date-fns'
import {
  MAX_AGENT_CLOSE_AMOUNT,
  PROPERTY_TOUR_SCHEDULE_STATUS,
  LOW_LEVEL_RELEASE_REASON,
  HIGH_LEVEL_RELEASE_REASON,
  RELEASE_REASONS,
  HIGH_LEVEL_TO_LOW_LEVELS_REASON,
} from 'common/constants'
import {
  selectAgentCloseAgentCommission,
  selectAgentCloseAmount,
  selectAgentCloseDate,
  selectAgentCloseMLS,
  selectAgentClosePropertyAddress,
  selectAgentContractAmount,
  selectAgentContractCloseDate,
  selectAgentContractMLS,
  selectAgentContractPropertyAddress,
  selectAgentStatus,
  selectIsLease,
  selectIsPropertyInquiryTourUpdateNeeded,
  selectPropertyInquiryTourOpId,
  selectPropertyInquiryRequestedTourStatus,
  selectSelectedSettlementProvider,
} from 'manager/selectors/referral_selectors'

import _ from 'lodash'
import {USE_NEW_SELECTION, USE_PRIOR_SELECTION} from "manager/redux/referral_status"

const selectReferralStatus = state => state.referralStatus

export const selectIsInterestedInTour = createSelector(selectReferralStatus, status => status.isInterestedInTour)

export const selectIsRequestedTourCompleted = createSelector(selectReferralStatus, status => status.isRequestedTourCompleted)

export const selectIsRequestedTourScheduled = createSelector(selectReferralStatus, status => status.isRequestedTourScheduled)

export const selectIsTourCompleted = createSelector(selectReferralStatus, status => status.isTourCompleted)

export const selectIsTourRescheduled = createSelector(selectReferralStatus, status => status.isTourRescheduled)

export const selectRescheduledTourDate = createSelector(selectReferralStatus, status => status.rescheduledTourDate)

export const selectRescheduledTourTime = createSelector(selectReferralStatus, status => status.rescheduledTourTime)

export const selectRequestedTourScheduledDate = createSelector(selectReferralStatus, status => status.requestedTourScheduledDate)

export const selectRequestedTourScheduledTime = createSelector(selectReferralStatus, status => status.requestedTourScheduledTime)

export const selectReleaseReasonHighLevel = createSelector(selectReferralStatus, status => status.releaseReasonHighLevel)

export const selectTourCompletedOpIds = createSelector(selectReferralStatus, status => status.tourCompletedOpIds)

export const selectIsEditingMortgageCompany = createSelector(selectReferralStatus, status => !!status.editMortgageCompany)

export const selectIsEditingTitleCompany = createSelector(selectReferralStatus, status => !!status.editTitleCompany)

export const selectIsRequestedTourScheduledBeforeToday = createSelector(
  selectRequestedTourScheduledDate,
  selectRequestedTourScheduledTime,
  (requestedTourScheduledDate, requestedTourScheduledTime) => {
    if (!requestedTourScheduledDate || !requestedTourScheduledTime) {
      return false
    }
    const requestedDateParsed = parse(`${requestedTourScheduledDate}T${requestedTourScheduledTime}`)
    return isBefore(requestedDateParsed, startOfToday())
  }
)

export const selectBlurredFields = createSelector(
  selectReferralStatus,
  (status) => status.blurredFields || new Set()
)

export const selectNote = createSelector(selectReferralStatus, status => status.note)

export const selectOfferStatus = createSelector(selectReferralStatus, status => status.offer_status)

export const selectIsNoteValid = createSelector(selectNote, note => !!note)

export const selectHasTourInfo = createSelector(
  selectIsPropertyInquiryTourUpdateNeeded,
  selectPropertyInquiryRequestedTourStatus,
  selectIsInterestedInTour,
  selectIsRequestedTourCompleted,
  selectIsRequestedTourScheduled,
  selectIsRequestedTourScheduledBeforeToday,
  selectIsTourCompleted,
  selectIsTourRescheduled,
  selectRescheduledTourDate,
  selectRescheduledTourTime,
  (isTourUpdateNeeded, tourStatus, isInterestedInTour, isRequestedTourCompleted, isRequestedTourScheduled, isRequestedTourScheduledBeforeToday, isTourCompleted, isTourRescheduled, rescheduledTourDate, rescheduledTourTime) => {
    if (!isTourUpdateNeeded) {
      return false
    }

    if (tourStatus === PROPERTY_TOUR_SCHEDULE_STATUS.REQUESTED) {
      if (isRequestedTourScheduled) {
        return (!isRequestedTourScheduledBeforeToday) || (isRequestedTourCompleted === true) || ((isRequestedTourCompleted === false) && (isInterestedInTour !== null))
      } else {
        return isInterestedInTour !== null
      }
    } else if (tourStatus === PROPERTY_TOUR_SCHEDULE_STATUS.SCHEDULED) {
      return (isTourCompleted || (isTourRescheduled && rescheduledTourDate && rescheduledTourTime))
    }

    return false
  }
)

export const selectHasTourInfoOrValidNote = createSelector(selectHasTourInfo, selectIsNoteValid, (hasTourInfo, isNoteValid) => hasTourInfo || isNoteValid)

export const selectPropertyTourQuestionsTourStatus = createSelector(
  selectPropertyInquiryRequestedTourStatus,
  selectIsInterestedInTour,
  selectIsRequestedTourCompleted,
  selectIsRequestedTourScheduled,
  selectIsRequestedTourScheduledBeforeToday,
  selectIsTourCompleted,
  selectIsTourRescheduled,
  (inquiryTourStatus, isInterestedInTour, isRequestedTourCompleted, isRequestedTourScheduled, isRequestedTourScheduledBeforeToday, isTourCompleted, isTourRescheduled) => {
    if ([PROPERTY_TOUR_SCHEDULE_STATUS.REQUESTED, PROPERTY_TOUR_SCHEDULE_STATUS.SUGGESTED].includes(inquiryTourStatus)) {
      if (!isRequestedTourScheduled) {
        return isInterestedInTour === false ? PROPERTY_TOUR_SCHEDULE_STATUS.CANCELLED : null
      }

      if (!isRequestedTourScheduledBeforeToday) {
        return PROPERTY_TOUR_SCHEDULE_STATUS.SCHEDULED
      }

      if (isRequestedTourCompleted) {
        return PROPERTY_TOUR_SCHEDULE_STATUS.COMPLETED
      }

      return isInterestedInTour ? PROPERTY_TOUR_SCHEDULE_STATUS.SCHEDULED : PROPERTY_TOUR_SCHEDULE_STATUS.CANCELLED
    }

    if (inquiryTourStatus === PROPERTY_TOUR_SCHEDULE_STATUS.SCHEDULED) {
      if (isTourCompleted) {
        return PROPERTY_TOUR_SCHEDULE_STATUS.COMPLETED
      }

      if (isTourCompleted !== false) {
        return null
      }

      if (isTourRescheduled === false) {
        return PROPERTY_TOUR_SCHEDULE_STATUS.UNKNOWN
      } else {
        return PROPERTY_TOUR_SCHEDULE_STATUS.SCHEDULED
      }
    }

    return null
})

export const selectPropertyTourQuestionsData = createSelector(
  selectIsPropertyInquiryTourUpdateNeeded,
  selectPropertyInquiryTourOpId,
  selectPropertyInquiryRequestedTourStatus,
  selectPropertyTourQuestionsTourStatus,
  selectIsRequestedTourScheduled,
  selectRequestedTourScheduledDate,
  selectRequestedTourScheduledTime,
  selectIsTourCompleted,
  selectIsTourRescheduled,
  selectRescheduledTourDate,
  selectRescheduledTourTime,
  selectTourCompletedOpIds,
  (
    isTourUpdateNeeded,
    tourOpId,
    inquiryTourStatus,
    questionsTourStatus,
    isRequestedTourScheduled,
    requestedTourScheduledDate,
    requestedTourScheduledTime,
    isTourCompleted,
    isTourRescheduled,
    rescheduledTourDate,
    rescheduledTourTime,
    tourCompletedOpIds,
  ) => {
    if (!isTourUpdateNeeded || !tourOpId || !questionsTourStatus) {
      return {}
    }

    const data = {
      tour_op_id: tourOpId,
      tour_op_ids: tourCompletedOpIds,
      tour_status: questionsTourStatus,
    }


    if ([PROPERTY_TOUR_SCHEDULE_STATUS.REQUESTED, PROPERTY_TOUR_SCHEDULE_STATUS.SUGGESTED].includes(inquiryTourStatus)) {
      if (isRequestedTourScheduled === true) {
        if (!requestedTourScheduledDate || !requestedTourScheduledTime) {
          return {}
        }

        data['tour_date'] = requestedTourScheduledDate
        data['tour_time'] = requestedTourScheduledTime
      }
    }

    if (inquiryTourStatus === PROPERTY_TOUR_SCHEDULE_STATUS.SCHEDULED) {
      if ((isTourCompleted === false) && (isTourRescheduled === true)) {
        if (!rescheduledTourDate || !rescheduledTourTime) {
          return {}
        }

        data['tour_date'] = rescheduledTourDate
        data['tour_time'] = rescheduledTourTime
      }
    }

    return data
})

export const selectOfferStatusValid = createSelector(selectOfferStatus, offerStatus => offerStatus && offerStatus.length > 0)

export const selectStatusClosedFields = createSelector(
  selectReferralStatus,
  selectNote,
  (referralStatus = {}, note) => {
    const {
      close_mls,
      close_property_address,
      close_amount,
      commission_amount,
      close_date,
      settlement,
      lender,
      loan_opt_out_company,
      loan_opt_out_officer,
      loan_opt_out_refer,
      loan_opt_out_email,
      loan_opt_out_phone,
      loan_opt_out_reasons,
      loan_opt_out_feedback = '',
      settlement_opt_out_company,
      settlement_opt_out_officer,
      settlement_opt_out_refer,
      settlement_opt_out_email,
      settlement_opt_out_phone,
      settlement_opt_out_reasons,
      settlement_opt_out_feedback = '',
      settlement_new_selection_company,
    } = referralStatus

    return {
      note,
      close_mls,
      close_property_address,
      close_amount,
      commission_amount,
      close_date,
      settlement,
      lender,
      ...(settlement !== 'optOut' ? {} : {
        settlement_opt_out_company,
        settlement_opt_out_officer,
        settlement_opt_out_refer,
        settlement_opt_out_email,
        settlement_opt_out_phone,
        settlement_opt_out_reasons,
        settlement_opt_out_feedback,
      }),
      ...(settlement !== USE_NEW_SELECTION ? {} : {
        settlement_new_selection_company
      }),
      ...(lender !== 'optOut' ? {} : {
        loan_opt_out_company,
        loan_opt_out_officer,
        loan_opt_out_refer,
        loan_opt_out_email,
        loan_opt_out_phone,
        loan_opt_out_reasons,
        loan_opt_out_feedback,
      }),
    }
  }
)

export const selectCloseMLS = createSelector(
  selectReferralStatus,
  selectAgentContractMLS,
  selectAgentCloseMLS,
  (referralStatus, contractMLS, closeMLS) => {
    if (typeof referralStatus.close_mls === 'string') {
      return referralStatus.close_mls
    }
    return closeMLS || contractMLS
  }
)

export const selectClosePropertyAddress = createSelector(
  selectReferralStatus,
  selectAgentContractPropertyAddress,
  selectAgentClosePropertyAddress,
  (referralStatus, contractPropertyAddress, closePropertyAddress) => {
    if (typeof referralStatus.close_property_address === 'string') {
      return referralStatus.close_property_address
    }
    return closePropertyAddress || contractPropertyAddress
  }
)

export const selectCloseAmount = createSelector(
  selectReferralStatus,
  selectAgentContractAmount,
  selectAgentCloseAmount,
  (referralStatus, contractCloseAmount, closeAmount) => {
    if (typeof referralStatus.close_amount === 'number') {
      return referralStatus.close_amount
    }
    return closeAmount || contractCloseAmount
  })

export const selectCommissionAmount = createSelector(
  selectReferralStatus,
  selectCloseAmount,
  selectIsLease,
  selectAgentCloseAgentCommission,
  (referralStatus, closeAmount, isLease, closeAgentCommission) => {
    if (typeof referralStatus.commission_amount === 'number') {
      return referralStatus.commission_amount
    }
    return closeAgentCommission || estimatedCommission(closeAmount, isLease)
  }
)

export const selectCloseDate = createSelector(
  selectReferralStatus,
  selectAgentContractCloseDate,
  selectAgentCloseDate,
  (referralStatus, contractCloseDate, closeDate) => referralStatus.close_date || closeDate || contractCloseDate
)

export const selectFinanaceType = createSelector(
  selectReferralStatus,
  referralStatus => referralStatus.financeType || 'financing'
)

export const selectCloseDateValid = createSelector(
  selectCloseDate,
  (closeDate) => {
    return !!closeDate && isBefore(parse(closeDate), new Date())
  }
)

export const selectClosedPage1FormIsValid = createSelector(
  selectIsLease,
  selectCloseMLS,
  selectClosePropertyAddress,
  selectCloseAmount,
  selectCommissionAmount,
  selectCloseDateValid,
  selectFinanaceType,
  (isLease, closeMLS, closePropertyAddress, closeAmount, commissionAmount, isCloseDateValid, financeType) => (
    (isLease || !!closeMLS) &&
    !!closePropertyAddress &&
    closeAmount > 0 &&
    closeAmount < MAX_AGENT_CLOSE_AMOUNT &&
    commissionAmount > 0 &&
    (isLease || commissionAmount < closeAmount) &&
    isCloseDateValid &&
    (isLease || !!financeType)
  )
)

export const selectCloseDateErrorMsg = createSelector(
  selectClosedPage1FormIsValid,
  selectCloseDateValid,
  selectCloseDate,
  selectBlurredFields,
  (isPage1FormValid, closeDateValid, closeDate, blurredFields) =>
    (!isPage1FormValid && !closeDateValid && (closeDate || blurredFields.has('close_date'))) ? 'Must enter past or present date' : ""
)

export const selectIsLenderValid = createSelector(
  selectReferralStatus,
  selectIsEditingMortgageCompany,
  (referralStatus, isEditing) => referralStatus.validLender && !isEditing
)

export const selectIsSettlementValid = createSelector(
  selectReferralStatus,
  selectIsEditingTitleCompany,
  (referralStatus, isEditing) => referralStatus.validSettlement && !isEditing
)

export const selectLender = createSelector(
  selectReferralStatus,
  referralStatus => referralStatus.lender
)

export const selectSettlement = createSelector(
  selectReferralStatus,
  referralStatus => referralStatus.settlement
)

export const selectIsSettlementSelectionPrior = createSelector(
  selectSettlement,
  closeSettlementType => closeSettlementType === USE_PRIOR_SELECTION
)

export const selectIsSettlementSelectionNew = createSelector(
  selectSettlement,
  closeSettlementType => closeSettlementType === USE_NEW_SELECTION
)

export const selectSettlementNewSelectionCompany = createSelector(
  selectReferralStatus,
  referralStatus => referralStatus.settlement_new_selection_company
)

export const selectIsCashBuy = createSelector(
  selectFinanaceType,
  financeType => financeType === 'cash'
)

export const selectIsUpdatingContract = createSelector(
  selectAgentStatus,
  agent_status => agent_status === 'contract'
)

export const selectContractMLS = createSelector(
  selectReferralStatus,
  selectAgentContractMLS,
  (referralStatus, agentContractMLS) => {
    if (typeof referralStatus.contract_mls === 'string') {
      return referralStatus.contract_mls
    }
    return agentContractMLS
  })

export const selectContractPropertyAddr = createSelector(
  selectReferralStatus,
  selectAgentContractPropertyAddress,
  (referralStatus, agentPropertyAddress) => {
    if (typeof referralStatus.contract_property_address === 'string') {
      return referralStatus.contract_property_address
    }
    return agentPropertyAddress || ''
})

export const selectContractAmount = createSelector(
  selectReferralStatus,
  selectAgentContractAmount,
  (referralStatus, agentContractAmount) => {
    if (typeof referralStatus.contract_amount === 'number') {
      return referralStatus.contract_amount
    }
    return agentContractAmount
  })

export const selectContractCloseDate = createSelector(
  selectReferralStatus,
  selectAgentContractCloseDate,
  (referralStatus, agentCloseDate) => referralStatus.contract_close_date || agentCloseDate
)

export const selectContractStatus = createSelector(
  selectReferralStatus,
  referralStatus => referralStatus.contract_status
)

export const selectValidationAttempted = createSelector(
  selectReferralStatus,
  referralStatus => referralStatus.validationAttempted
)

export const selectValidContractMLS = createSelector(
  selectReferralStatus,
  referralStatus => referralStatus.validContractMLS
)

export const selectContractMlsErrorMsg = createSelector(
  selectValidationAttempted,
  selectValidContractMLS,
  selectContractMLS,
  selectBlurredFields,
  (validationAttempted, validContractMLS, contractMLS, blurredFields) =>
    (validationAttempted && !validContractMLS && (contractMLS || blurredFields.has('contract_mls'))) ? "Required field" : ""
)

export const selectValidContractAmended = createSelector(
  selectReferralStatus,
  selectValidationAttempted,
  (referralStatus, validationAttempted) => referralStatus.validContractAmended || !validationAttempted
)

export const selectValidContractAmount = createSelector(
  selectReferralStatus,
  referralStatus => referralStatus.validContractAmount
)

export const selectContractAmountErrorMsg = createSelector(
  selectValidationAttempted,
  selectValidContractAmount,
  selectContractAmount,
  selectBlurredFields,
  (validationAttempted, validContractAmount, contractAmount, blurredFields) =>
    (validationAttempted && !validContractAmount && (contractAmount || blurredFields.has('contract_amount'))) ? "Required field" : ""
)

export const selectValidContractCloseDate = createSelector(
  selectReferralStatus,
  referralStatus => referralStatus.validContractCloseDate
)

export const selectContractCloseDateErrorMsg = createSelector(
  selectValidationAttempted,
  selectValidContractCloseDate,
  selectContractCloseDate,
  selectBlurredFields,
  (validationAttempted, validContractCloseDate, contractCloseDate, blurredFields) =>
    (validationAttempted && !validContractCloseDate && (contractCloseDate || blurredFields.has('contract_close_date'))) ? "Must enter future date" : ""
)

export const selectValidContractPropertyAddress = createSelector(
  selectReferralStatus,
  referralStatus => referralStatus.validContractPropertyAddress
)

export const selectValidContractStatus = createSelector(
  selectReferralStatus,
  referralStatus => referralStatus.validContractStatus
)

export const selectHoldTerm = createSelector(
  selectReferralStatus,
  referralStatus => referralStatus.hold_term || ''
)

export const selectReleaseReason = createSelector(
  selectReferralStatus,
  referralStatus => referralStatus.release_reason || ''
)

export const selectTransactionCount = createSelector(
  selectReferralStatus,
  referralStatus => _.size(referralStatus.assignment_transactions) || 0
)

export const selectReleaseIsOffensiveBehavior = createSelector(
  selectReleaseReasonHighLevel,
  highLevelReleaseReason => highLevelReleaseReason === HIGH_LEVEL_RELEASE_REASON.INAPPROPRIATE_BEHAVIOR
)

export const selectReleaseIsLostContact = createSelector(
  selectReleaseReasonHighLevel,
  highLevelReleaseReason => highLevelReleaseReason === HIGH_LEVEL_RELEASE_REASON.LOST_CONTACT
)

export const selectReleaseReasonIsNotListed = createSelector(
  selectReleaseReasonHighLevel,
  (highLevelReleaseReason) => highLevelReleaseReason === LOW_LEVEL_RELEASE_REASON.OTHER
)

export const selectReleaseFeedbackLabel = createSelector(
  selectReleaseIsOffensiveBehavior,
  selectReleaseReasonIsNotListed,
  (isOffensiveBehvior, isNotListed) => isNotListed ? 'Note' : isOffensiveBehvior ? 'Details of behavior': 'Note (Optional)'
)

export const selectReleaseSubmitDisabled = createSelector(
  selectReleaseReason,
  selectReleaseIsOffensiveBehavior,
  selectNote,
  (release_reason, isOffensiveBehavior, note) => {
    if (isOffensiveBehavior || release_reason === 'other') {
      return !note
    }
    return !release_reason
  }
)

export const selectReleaseShowMultiWarning = createSelector(
  selectReleaseIsOffensiveBehavior,
  selectTransactionCount,
  (isOffensiveBehvior, transactionCount) => !isOffensiveBehvior && transactionCount > 1
)

export const selectReleaseHighLevelOptions = createSelector(
  () => {
    // this is a workaround to allow the new sub-reasons to appear in other products as a flattened list,
    // but still show it as an umbrella "high level reason" w/speedbump buttons even when speedbump feature flag is not set
    const offensiveBehaviorSubReasons = HIGH_LEVEL_TO_LOW_LEVELS_REASON[HIGH_LEVEL_RELEASE_REASON.INAPPROPRIATE_BEHAVIOR]

    return RELEASE_REASONS.filter(({ value }) => !offensiveBehaviorSubReasons.includes(value))
  }
)

export const selectReleaseShouldShowSubReasons = createSelector(
  selectReleaseIsOffensiveBehavior,
  (isOffensiveBehvior) => {
    return !!(isOffensiveBehvior)
  }
)

export const selectReleaseLowLevelOptions = createSelector(
  selectReleaseReasonHighLevel,
  selectReleaseShouldShowSubReasons,
  (highLevelReleaseReason, shouldShowSubReasons) => {
    if (shouldShowSubReasons && highLevelReleaseReason) {
      return HIGH_LEVEL_TO_LOW_LEVELS_REASON[highLevelReleaseReason] || []
    }
    return []
  }
)

export const selectReleaseShouldShowNoteAndReconnect = createSelector(
  selectReleaseReasonHighLevel,
  selectReleaseReason,
  selectReleaseIsOffensiveBehavior,
  (highLevelReleaseReason, release_reason, isOffensiveBehavior) => {
    if (isOffensiveBehavior) {
      // since offensive behavior ignores speed bump flag it will set both high level and low level when chosen
      // when the user selects a sub-reason the release_reason will change and we enable the note field (which is required for submit)
      return release_reason !== highLevelReleaseReason
    }
    return !!release_reason
  }
)

export const selectSelectedTitleCompany = createSelector(
  selectSelectedSettlementProvider,
  selectIsSettlementSelectionPrior,
  selectSettlementNewSelectionCompany,
  (selectedSettlementProvider, isSettlementSelectionPrior, newSelectionCompany) => {
    if (isSettlementSelectionPrior && selectedSettlementProvider) {
      return {
        'id': selectedSettlementProvider.qualia_agency_id,
        'name': selectedSettlementProvider.settlement_provider_name
      }
    } else if (newSelectionCompany) {
      return {'id': newSelectionCompany.id, 'name': newSelectionCompany.name}
    }

    return null
  }
)
